import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { CircularProgress } from '@mui/material'
import { Icon as Iconify } from '@iconify/react'
import { appColors } from 'utils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { getDetails } from './AddEditAlertGroupsDefs'
import DataConfigParameterDisplay from 'components/WellList/DataConfigModal/DataConfigParameterDisplay'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const AddEditAlertGroupsModal = ({
  open,
  mode,
  initialData,
  onClose,
  onSubmit,
  getUnitsForOrg,
  showSpinner,
  organization,
  existingGroups = [],
  rigs = [],
  operators = [],
}) => {
  const { theme, getTextColor, getDisabledColor, getCardBackground, getBackColor, getWindowBarColor } = useInnovaTheme()

  const textColor = getTextColor()
  const cardBackground = getCardBackground()
  const [form, setForm] = useState({
    uid: '',
    groupName: '',
    organization: organization,
    unitPrefs: '',
    distributionList: [],
    rigs: [],
    operators: [],
    alerts: {},
  })

  const [errors, setErrors] = useState({})
  const [emailInput, setEmailInput] = useState('')

  useEffect(() => {
    if (!form.organization) return

    const units = getUnitsForOrg(form.organization)

    if (form.unitPrefs && units.includes(form.unitPrefs)) {
      return
    }

    setForm((prev) => ({
      ...prev,
      unitPrefs: units.length > 0 ? units[0] : '',
    }))
  }, [form.organization, form.unitPrefs, getUnitsForOrg])

  const handleChange = (field, value) => {
    if (field === 'organization' && value !== form.organization) {
      setForm((prev) => ({
        ...prev,
        unitPrefs: '',
        organization: value,
      }))

      return
    }

    setForm((prev) => {
      const nextForm = { ...prev, [field]: value }

      return nextForm
    })
  }

  const handleGroupDetailChange = (field, values) => {
    const selectedValues = [...new Set(values.map((item) => item?.value ?? item))]
    const previousValues = form[field] ?? []

    const allWasSelected = previousValues.includes('All')
    const allIsSelected = selectedValues.includes('All')

    let newValues = selectedValues

    if (!allWasSelected && allIsSelected) {
      // All was just selected, so remove everything else.
      newValues = ['All']
    } else if (allWasSelected && selectedValues.length > 1) {
      // Another option was selected while All was already selected.
      newValues = selectedValues.filter((value) => value !== 'All')
    }

    setForm((prev) => ({
      ...prev,
      [field]: newValues,
    }))
  }

  const duplicateGroupName = useMemo(() => {
    const normalizedGroupName = form.groupName?.trim().toLowerCase()
    const normalizedOrganization = form.organization?.trim().toLowerCase()

    if (!normalizedGroupName || !normalizedOrganization) return false

    return existingGroups.some((group) => {
      if (mode === 'edit' && group.uid === form.uid) {
        return false
      }

      return (
        group.groupName?.trim().toLowerCase() === normalizedGroupName &&
        group.organization?.trim().toLowerCase() === normalizedOrganization
      )
    })
  }, [existingGroups, form.groupName, form.organization, form.uid, mode])

  const isValid = useMemo(() => {
    if (!form.groupName?.trim()) return false
    if (!form.organization) return false
    if (!form.unitPrefs) return false
    if (!Array.isArray(form.distributionList) || form.distributionList.length === 0) return false
    if (form.distributionList.some((email) => !emailRegex.test(email))) return false
    if (duplicateGroupName) return false

    return true
  }, [form, duplicateGroupName])

  const handleSubmit = () => {
    if (!isValid || !hasChanges) return

    const payload = {
      uid: form.uid,
      groupName: form.groupName.trim(),
      organization: form.organization,
      groupDetails: {
        distributionList: form.distributionList,
        unitPrefs: form.unitPrefs,
        rigs: form.rigs ?? [],
        operators: form.operators ?? [],
        alerts: form.alerts ?? {},
      },
    }

    onSubmit(payload)
  }

  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && initialData) {
      setForm({
        uid: initialData.uid,
        groupName: initialData.groupName ?? '',
        organization: initialData.organization ?? organization,
        unitPrefs: initialData.groupDetails?.unitPrefs ?? '',
        distributionList: initialData.groupDetails?.distributionList ?? [],
        rigs: initialData.groupDetails?.rigs ?? [],
        operators: initialData.groupDetails?.operators ?? [],
        alerts: initialData.groupDetails?.alerts ?? {},
      })

      setEmailInput('')
      setErrors({})
      return
    }

    const units = getUnitsForOrg(organization)

    setForm({
      uid: '',
      groupName: '',
      organization,
      unitPrefs: units.length > 0 ? units[0] : '',
      distributionList: [],
      rigs: [],
      operators: [],
      alerts: {},
    })

    setEmailInput('')
    setErrors({})
  }, [open, mode, initialData, organization, getUnitsForOrg])

  const hasChanges = useMemo(() => {
    if (mode === 'add') return true
    if (!initialData) return false

    if (form.groupName !== (initialData.groupName ?? '')) return true
    if (form.organization !== (initialData.organization ?? '')) return true
    if (form.unitPrefs !== (initialData.groupDetails?.unitPrefs ?? '')) return true

    const normalizeArray = (values) => [...(values ?? [])].sort()

    const currentEmails = normalizeArray(form.distributionList)
    const originalEmails = normalizeArray(initialData.groupDetails?.distributionList)

    if (JSON.stringify(currentEmails) !== JSON.stringify(originalEmails)) {
      return true
    }

    if (JSON.stringify(normalizeArray(form.rigs)) !== JSON.stringify(normalizeArray(initialData.groupDetails?.rigs))) {
      return true
    }

    if (
      JSON.stringify(normalizeArray(form.operators)) !==
      JSON.stringify(normalizeArray(initialData.groupDetails?.operators))
    ) {
      return true
    }

    return false
  }, [form, initialData, mode])

  const cancelLabel = useMemo(() => {
    if (mode === 'edit' && !hasChanges) {
      return 'Close'
    }
    return 'Cancel'
  }, [mode, hasChanges])

  const handleAddEmail = () => {
    const enteredEmails = emailInput
      .split(/[;,]/)
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean)

    if (enteredEmails.length === 0) return

    const invalidEmails = enteredEmails.filter((email) => !emailRegex.test(email))

    if (invalidEmails.length > 0) {
      setErrors((prev) => ({
        ...prev,
        emailInput: `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`,
      }))
      return
    }

    const existingEmails = new Set(form.distributionList.map((email) => email.toLowerCase()))
    const duplicateEmails = enteredEmails.filter((email) => existingEmails.has(email))
    const emailsToAdd = [...new Set(enteredEmails)].filter((email) => !existingEmails.has(email))

    if (emailsToAdd.length === 0) {
      setErrors((prev) => ({
        ...prev,
        emailInput: 'All entered emails are already in the distribution list',
      }))
      return
    }

    setForm((prev) => ({
      ...prev,
      distributionList: [...prev.distributionList, ...emailsToAdd],
    }))

    setEmailInput('')

    setErrors((prev) => ({
      ...prev,
      emailInput:
        duplicateEmails.length > 0
          ? `Skipped existing email${duplicateEmails.length > 1 ? 's' : ''}: ${duplicateEmails.join(', ')}`
          : '',
    }))
  }

  const handleRemoveEmail = (emailToRemove) => {
    setForm((prev) => ({
      ...prev,
      distributionList: prev.distributionList.filter((email) => email !== emailToRemove),
    }))
  }

  const inputSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiInputBase-root': {
        minHeight: 40,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
    }),
    [getBackColor, getTextColor, getDisabledColor, theme],
  )

  const autoCompleteSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiOutlinedInput-root': {
        minHeight: 40,
        minWidth: 0,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
      '& .MuiChip-root': {
        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        color: getTextColor(),
      },
      '& .MuiChip-deleteIcon': {
        color: getDisabledColor(),
      },
    }),
    [getBackColor, getTextColor, getDisabledColor, theme],
  )

  const autoCompleteSlotProps = useMemo(
    () => ({
      paper: {
        sx: {
          backgroundColor: cardBackground,
          color: textColor,
          border: theme === 'dark' ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.10)',
        },
      },
      popper: {
        sx: {
          '& .MuiAutocomplete-listbox': {
            backgroundColor: cardBackground,
            color: textColor,
            padding: 0,
          },
          '& .MuiAutocomplete-option': {
            color: textColor,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
          },
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          },
        },
      },
    }),
    [cardBackground, textColor, theme],
  )

  const renderEmailInput = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
        minWidth: 0,
      }}>
      <TextField
        fullWidth
        size='small'
        value={emailInput}
        onChange={(event) => {
          setEmailInput(event.target.value)

          if (errors.emailInput) {
            setErrors((prev) => ({
              ...prev,
              emailInput: '',
            }))
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            handleAddEmail()
          }
        }}
        error={Boolean(errors.emailInput)}
        helperText={errors.emailInput}
        sx={inputSx}
      />

      <Button
        variant='outlined'
        onClick={handleAddEmail}
        disabled={!emailInput.trim()}
        sx={{
          minWidth: 90,
          height: 40,
          flexShrink: 0,
        }}>
        Add
      </Button>
    </Box>
  )

  const renderDistributionList = () => (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        width: '100%',
        minHeight: 56,
        maxHeight: 140,
        overflowY: 'auto',
        p: 1,
        border: '1px solid',
        borderColor: getDisabledColor(),
        borderRadius: 1,
        backgroundColor: getBackColor(),
        alignItems: 'flex-start',
        alignContent: 'flex-start',
      }}>
      {form.distributionList.length === 0 ? (
        <Typography
          sx={{
            color: getDisabledColor(),
            fontStyle: 'italic',
            py: 0.5,
          }}>
          No email addresses added
        </Typography>
      ) : (
        form.distributionList.map((email) => (
          <Chip
            key={email}
            label={email}
            size='small'
            onDelete={() => handleRemoveEmail(email)}
            sx={{
              backgroundColor: cardBackground,
              color: getTextColor(),
              '& .MuiChip-deleteIcon': {
                color: getDisabledColor(),
              },
            }}
          />
        ))
      )}
    </Box>
  )

  const renderRigs = () => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size='small'
      options={rigs}
      value={rigs.filter((option) => (form.rigs ?? []).includes(option.value))}
      getOptionLabel={(option) => option?.label ?? option ?? ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(_, nextValues) => handleGroupDetailChange('rigs', nextValues)}
      sx={autoCompleteSx}
      slotProps={autoCompleteSlotProps}
      renderInput={(params) => <TextField {...params} />}
    />
  )

  const renderOperators = () => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size='small'
      options={operators}
      value={operators.filter((option) => (form.operators ?? []).includes(option.value))}
      getOptionLabel={(option) => option?.label ?? option ?? ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(_, nextValues) => handleGroupDetailChange('operators', nextValues)}
      sx={autoCompleteSx}
      slotProps={autoCompleteSlotProps}
      renderInput={(params) => <TextField {...params} />}
    />
  )

  const detailsDefs = getDetails(form, getUnitsForOrg(form.organization), duplicateGroupName, {
    emailInput: renderEmailInput,
    distributionList: renderDistributionList,
    rigs: renderRigs,
    operators: renderOperators,
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        autoComplete: 'off',
        component: 'form',
        sx: {
          backgroundColor: getWindowBarColor(),
          width: '850px',
          maxWidth: '95vw',
          maxHeight: '90vh',
        },
      }}>
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            flex: 1,
          }}>
          <Iconify icon={mode === 'edit' ? 'mdi:pencil-outline' : 'fluent:add-12-filled'} />

          {mode === 'edit' ? 'Edit Alert Group' : 'Add Alert Group'}
        </Typography>

        <IconButton onClick={onClose} size='small'>
          <Iconify icon='mdi:close' />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 1.5,
          backgroundColor: getBackColor(),
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '100%',
            minWidth: 0,
          }}>
          <DataConfigParameterDisplay
            title='Alert Group Details'
            defs={detailsDefs.groupDetails}
            onChange={({ tag, value }) => handleChange(tag, value)}
            labelWidth={115}
            editorMaxWidth={690}
          />

          <DataConfigParameterDisplay
            title='Distribution List'
            defs={detailsDefs.distributionList}
            labelWidth={115}
            editorMaxWidth={690}
          />

          <DataConfigParameterDisplay
            title='Alert Group Selections'
            defs={detailsDefs.selections}
            labelWidth={115}
            editorMaxWidth={690}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: getWindowBarColor(),
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          {cancelLabel}
        </Button>

        <Button
          variant='outlined'
          color='primary'
          onClick={handleSubmit}
          disabled={!isValid || !hasChanges}
          sx={{
            pointerEvents: showSpinner ? 'none' : 'auto',
          }}
          startIcon={showSpinner ? <CircularProgress size={18} color='inherit' /> : null}>
          {showSpinner ? (mode === 'edit' ? 'Saving...' : 'Creating...') : mode === 'edit' ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddEditAlertGroupsModal
